import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {BotMode, ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';

import {IStore} from 'redux/interface';
import {assistantAsyncActions, getAssistantDialogue, getTranslations} from 'redux/pageAsyncActions';
import {assistantDialogueClearedAction} from 'redux/assistant/actions';
import {closeArticle} from 'redux/content/actions';

import AvatarDialogue from 'components/avatar-dialogue';
import BotChat from 'components/bot-chat';
import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {BotType} from 'utils/hooks/use-chat-bot/interfaces';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import ChatIcon from 'assets/image/menu-auth/chat.svg';
import NotFoundImg from 'assets/image/common/skill-crossroad.svg';

import './styles.scss';

// 'interactive' renders the avatar video surface and 'voice_only' the voice surface; every other kind a text chat.
const avatarSurfaceKinds = ['interactive', 'voice_only'];

export const Assistant: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const {settings} = useSelector((state: IStore) => state.auth);
  const {loading} = useSelector((state: IStore) => state.status);
  const {status, assistant} = useSelector((state: IStore) => state.assistant);
  const [avatarSessionKey, setAvatarSessionKey] = useState(0);

  const pageConfig = useMemo(() => {
    return settings?.pages.find(
      (p) => p.name.toLowerCase() === Pages.Assistant.toLowerCase()
    );
  }, [settings]);

  const translationKey = pageConfig?.translationKey || TranslationKeys.Assistant;
  const title = pageConfig?.title || 'Assistant';

  useGetTranslations({translationKey});

  function loadDialogue() {
    dispatch(assistantAsyncActions[getAssistantDialogue]());
  }

  useEffect(() => {
    dispatch(closeArticle());
    loadDialogue();

    return () => {
      // Reset to 'idle' so the next visit never renders the previous result before its own
      // request resolves.
      dispatch(assistantDialogueClearedAction());
    };
  }, []);

  function renderBody() {
    switch (status) {
      case 'assigned':
        if (!assistant) {
          return null;
        }

        if (avatarSurfaceKinds.includes(assistant.avatarKind)) {
          // Remounting on end returns the participant to the lobby; nothing starts again until Start.
          return <AvatarDialogue
            key={avatarSessionKey}
            dialogueId={assistant.dialogueId}
            botId={assistant.botId}
            avatarId={assistant.avatarId}
            apId={assistant.apId}
            title={assistant.interventionTitle || title}
            voiceOnly={assistant.avatarKind === 'voice_only'}
            onEnd={() => setAvatarSessionKey(key => key + 1)}
          />;
        }

        return (
          <BotChat
            chatType={BotType.Dialogue}
            chatMode={BotMode.FullHistory}
            dialogueId={assistant.dialogueId}
            apId={assistant.apId}
            title={assistant.interventionTitle || title}
            tabsEnabled={settings?.chatSettings?.tabsEnabled || false}
          />
        );
      case 'unassigned':
        return <div className="assistant__empty">
          <NotFoundInfo
            title={intl.formatMessage({
              id: 'gritx.assistant.noAssignments',
              defaultMessage: 'There are no assignments for you.'
            })}
            image={NotFoundImg}
          />
        </div>;
      case 'unavailable':
        return <div className="assistant__unavailable" role="alert">
          <p className="assistant__unavailable-text">
            <FormattedMessage
              id="gritx.assistant.unavailable"
              defaultMessage="We are unable to load your assistant right now. This is a problem on our side. Please try again."
            />
          </p>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.assistant.retry',
              defaultMessage: 'Try again'
            })}
            variant={ButtonVariant.Primary}
            className="assistant__retry-btn"
            onClick={loadDialogue}
          />
        </div>;
      // 'idle' and 'loading'. Never falls through to the empty state.
      default:
        return <Loader nested/>;
    }
  }

  if (loading.includes(getTranslations)) {
    return <section className="assistant container">
      <Loader/>
    </section>;
  }

  return (
    <section className="assistant container">
      <h1 className="assistant__title">
        <img src={ChatIcon} alt="" className="assistant__icon"/>
        <FormattedMessage
          id={`gritx.common.navigation.${title}`}
          defaultMessage={title}
        />
      </h1>
      {renderBody()}
    </section>
  );
};
